import {KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type Ref, useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react'

import styles from './ArtifactsTabBar.module.css'

export type TabItem = {
  id: string
  label: string
}

type ArtifactsTabBarProps = {
  tabs: TabItem[]
  activeId: string | null
  onActivate: (id: string) => void
  /**
   * Called when the user picks a tab from the overflow menu.
   * Implementations should move the chosen tab to just before any other overflow
   * tabs so it becomes the last visible tab.
   */
  onPromoteOverflowTab: (id: string) => void
  /** Optional per-tab close handler. When present, tabs show an X on hover. */
  onCloseTab?: (id: string) => void
  /** Rendered before the tabs, e.g. navigation back to a containing view. */
  leadingAction?: React.ReactNode
  /** Rendered to the right of the overflow kebab, e.g. per-artifact actions. */
  trailingActions?: React.ReactNode
  /**
   * Hard cap on how many tabs render inline before overflowing into the menu,
   * regardless of available width. Used by the narrow overlay to stay tidy.
   */
  maxVisibleTabs?: number
  activeTabRef?: Ref<HTMLButtonElement>
}

const TabLabelButton = memo(function TabLabelButtonInner({
  tab,
  active,
  onActivate,
  onKeyDown,
  closable,
  activeTabRef,
}: {
  tab: TabItem
  active: boolean
  onActivate: (id: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  closable?: boolean
  activeTabRef?: Ref<HTMLButtonElement>
}) {
  const labelRef = useRef<HTMLSpanElement>(null)
  const [overflowing, setOverflowing] = useState(false)
  useLayoutEffect(() => {
    const el = labelRef.current
    if (!el) return
    let raf = 0
    const check = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const next = el.scrollWidth > el.clientWidth + 1
        setOverflowing(prev => (prev === next ? prev : next))
      })
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [tab.label])
  return (
    <button
      ref={active ? activeTabRef : undefined}
      type="button"
      role="tab"
      id={`artifact-tab-${tab.id}`}
      data-tab-id={tab.id}
      tabIndex={active ? 0 : -1}
      aria-selected={active}
      aria-controls={active ? `artifact-panel-${tab.id}` : undefined}
      aria-keyshortcuts={closable ? 'Delete' : undefined}
      className={clsx(styles.Tab, active && styles.activeTab)}
      onClick={() => onActivate(tab.id)}
      onKeyDown={onKeyDown}
      aria-label={overflowing ? tab.label : undefined}
      data-overflowing={overflowing || undefined}
    >
      <span ref={labelRef} className={styles.TabLabel}>
        {tab.label}
      </span>
    </button>
  )
})

export function ArtifactsTabBar({
  tabs,
  activeId,
  onActivate,
  onPromoteOverflowTab,
  onCloseTab,
  leadingAction,
  trailingActions,
  maxVisibleTabs,
  activeTabRef,
}: ArtifactsTabBarProps) {
  const measureRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const leadingActionRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState(0)
  const [leadingActionWidth, setLeadingActionWidth] = useState(0)
  const [tabWidths, setTabWidths] = useState<Record<string, number>>({})
  // When the user closes a tab via the X (or Delete), the close target loses
  // focus to <body>. Set this flag so the next render can focus the new active
  // tab in a layout effect, keeping keyboard users anchored inside the bar.
  const refocusActiveTabRef = useRef(false)

  // Callback ref (not an effect): the bar only mounts once tabs exist, so a
  // []-deps effect would run while the node is still null and never re-attach.
  // A callback ref fires exactly when the node mounts/unmounts.
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    setAvailableWidth(node.getBoundingClientRect().width)
    let raf = 0
    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return
      // Coalesce bursts into one rAF and bail when unchanged to avoid
      // redundant re-renders (and the overflow re-pack) on Safari.
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const w = entry.contentRect.width
        setAvailableWidth(prev => (prev === w ? prev : w))
      })
    })
    observer.observe(node)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [])

  useLayoutEffect(() => {
    const el = leadingActionRef.current
    if (!el) {
      setLeadingActionWidth(0)
      return
    }
    const read = () => setLeadingActionWidth(el.getBoundingClientRect().width)
    read()
    const observer = new ResizeObserver(read)
    observer.observe(el)
    return () => observer.disconnect()
  }, [leadingAction])

  // Measure each tab at its natural (content) width from an off-screen row that
  // mirrors real tab styling — including the max-width cap. Greedy packing then
  // uses these real widths so short titles stay narrow and long ones cap/fade,
  // instead of forcing every tab to a uniform minimum.
  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el) return
    const read = () => {
      const next: Record<string, number> = {}
      // Single read-only pass over the measure row: all layout reads happen
      // together with no interleaved writes, so the browser reflows just once.
      for (const child of Array.from(el.children)) {
        const node = child as HTMLElement
        const id = node.getAttribute('data-tab-id')
        // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-loop -- batched read-only pass, no interleaved writes
        if (id) next[id] = node.offsetWidth
      }
      setTabWidths(prev => {
        const keys = Object.keys(next)
        if (keys.length === Object.keys(prev).length && keys.every(k => prev[k] === next[k])) return prev
        return next
      })
    }
    read()
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [tabs])

  // Split tabs into visible/overflow by greedily packing measured widths into
  // the available width (reserving room for trailing actions, plus the kebab
  // once overflow is needed). An optional maxVisibleTabs cap further limits the
  // inline count regardless of width.
  const {visibleTabs, overflowTabs} = useMemo(() => {
    if (tabs.length === 0) return {visibleTabs: [] as TabItem[], overflowTabs: [] as TabItem[]}
    const cap = maxVisibleTabs ?? Number.POSITIVE_INFINITY
    const measured = tabs.every(t => tabWidths[t.id] != null)

    let maxFit: number
    if (availableWidth <= 0 || !measured) {
      // Pre-measurement (SSR/first paint): honor the cap, otherwise show all.
      maxFit = Math.min(tabs.length, cap)
    } else {
      const trailingReserve = 80 // trailing actions (Start work, close X) + padding
      const kebabReserve = 36
      const tabGap = 4
      const packCount = (budget: number) => {
        let used = 0
        let count = 0
        for (let i = 0; i < tabs.length; i++) {
          const tab = tabs[i]
          if (!tab) break
          used += (tabWidths[tab.id] ?? 0) + (i > 0 ? tabGap : 0)
          if (used > budget) break
          count++
        }
        return count
      }
      let fit = packCount(availableWidth - leadingActionWidth - trailingReserve)
      if (fit < tabs.length) {
        // Overflow needed — re-pack with room for the kebab.
        fit = packCount(availableWidth - leadingActionWidth - trailingReserve - kebabReserve)
      }
      maxFit = Math.min(Math.max(1, fit), cap)
    }

    if (maxFit >= tabs.length) {
      return {visibleTabs: tabs, overflowTabs: [] as TabItem[]}
    }
    const visible = tabs.slice(0, maxFit)
    const overflow = tabs.slice(maxFit)
    // If active tab landed in overflow, swap it into the last visible slot.
    if (activeId && !visible.some(t => t.id === activeId)) {
      const overflowIdx = overflow.findIndex(t => t.id === activeId)
      if (overflowIdx !== -1 && visible.length > 0) {
        const lastIdx = visible.length - 1
        const last = visible[lastIdx]
        const promoted = overflow[overflowIdx]
        if (last && promoted) {
          visible[lastIdx] = promoted
          overflow[overflowIdx] = last
        }
      }
    }
    return {visibleTabs: visible, overflowTabs: overflow}
  }, [tabs, availableWidth, activeId, leadingActionWidth, maxVisibleTabs, tabWidths])

  const handleOverflowSelect = useCallback(
    (id: string) => {
      onPromoteOverflowTab(id)
    },
    [onPromoteOverflowTab],
  )

  // Roving tabindex: arrow keys move focus (and activation) between the
  // role="tab" buttons, per the ARIA Tabs keyboard pattern. The handler lives on
  // the (focusable) tab buttons and walks to its siblings via the tablist.
  // Delete/Backspace closes the focused tab when the parent provides onCloseTab.
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const {key} = e
      if (key === 'Delete' || key === 'Backspace') {
        if (!onCloseTab) return
        const id = e.currentTarget.getAttribute('data-tab-id')
        if (!id) return
        e.preventDefault()
        refocusActiveTabRef.current = true
        onCloseTab(id)
        return
      }
      if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') return
      const tablist = e.currentTarget.closest('[role="tablist"]')
      if (!tablist) return
      const tabEls = Array.from(tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]'))
      if (tabEls.length === 0) return
      const currentIdx = tabEls.findIndex(el => el === document.activeElement)
      let nextIdx: number
      if (key === 'Home') nextIdx = 0
      else if (key === 'End') nextIdx = tabEls.length - 1
      else if (key === 'ArrowRight') nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % tabEls.length
      else nextIdx = currentIdx < 0 ? tabEls.length - 1 : (currentIdx - 1 + tabEls.length) % tabEls.length
      const nextEl = tabEls[nextIdx]
      if (!nextEl) return
      e.preventDefault()
      nextEl.focus()
      const id = nextEl.getAttribute('data-tab-id')
      if (id) onActivate(id)
    },
    [onActivate, onCloseTab],
  )

  const handleCloseClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
      e.stopPropagation()
      if (!onCloseTab) return
      refocusActiveTabRef.current = true
      onCloseTab(id)
    },
    [onCloseTab],
  )

  // After a close, move focus to the new active tab. Runs only when the close
  // path armed the ref, so unrelated activeId changes (mouse clicks, etc.)
  // don't steal focus from wherever the user moved it.
  useLayoutEffect(() => {
    if (!refocusActiveTabRef.current) return
    refocusActiveTabRef.current = false
    const root = tabsRef.current
    if (!root) return
    if (activeId) {
      const target = root.querySelector<HTMLButtonElement>(`[role="tab"][data-tab-id="${CSS.escape(activeId)}"]`)
      target?.focus()
    }
  }, [activeId, tabs])

  if (tabs.length === 0) return null

  return (
    <div ref={containerRef} className={styles.TabBar}>
      {/* Off-screen row measuring each tab's natural width for overflow packing. */}
      <div ref={measureRef} className={styles.MeasureRow} aria-hidden="true">
        {tabs.map(tab => (
          <div key={tab.id} data-tab-id={tab.id} className={styles.TabContainer}>
            <span className={styles.Tab}>
              <span className={styles.TabLabel}>{tab.label}</span>
            </span>
          </div>
        ))}
      </div>
      {leadingAction ? (
        <div ref={leadingActionRef} className={styles.LeadingAction}>
          {leadingAction}
        </div>
      ) : null}
      <div ref={tabsRef} className={styles.Tabs} role="tablist" aria-label="Open artifacts">
        {visibleTabs.map(tab => (
          <div key={tab.id} className={clsx(styles.TabContainer, tab.id === activeId && styles.activeTabContainer)}>
            <TabLabelButton
              tab={tab}
              active={tab.id === activeId}
              onActivate={onActivate}
              onKeyDown={handleTabKeyDown}
              closable={Boolean(onCloseTab)}
              activeTabRef={activeTabRef}
            />
            {onCloseTab && (
              <button
                type="button"
                className={styles.TabCloseButton}
                aria-label={`Close ${tab.label}`}
                onClick={e => handleCloseClick(e, tab.id)}
              >
                <XIcon size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
      {overflowTabs.length > 0 && (
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton icon={KebabHorizontalIcon} variant="invisible" aria-label="More artifacts" />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay align="end">
            <ActionList>
              {overflowTabs.map(tab => (
                <ActionList.Item key={tab.id} onSelect={() => handleOverflowSelect(tab.id)}>
                  {tab.label}
                </ActionList.Item>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {trailingActions ? <div className={styles.TrailingActions}>{trailingActions}</div> : null}
    </div>
  )
}
